/**
 * Trigger node — navy solid pill with cream text and a lightning glyph.
 *
 * Section 5 of the redesign doc specifies the trigger node visual:
 *   "rounded-lg, navy solid, cream text, lightning icon".
 *
 * The trigger spec is passed in as the node `data`. We render a one-line
 * descriptive label that the server already computed (`label`) plus a
 * compact secondary line that summarises the trigger kind for quick
 * recognition.
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { TriggerRfNode } from '../layout.js';

export function TriggerNode({ data }: NodeProps<TriggerRfNode>) {
  const kind = data.trigger.type;
  const sub =
    kind === 'time' ? 'Time trigger' : kind === 'event' ? 'Event trigger' : 'Condition trigger';

  return (
    <div
      className="rounded-lg bg-brand-navy-700 px-3 py-2 text-brand-cream shadow-md ring-1 ring-brand-navy-900/30"
      style={{ minWidth: 200, maxWidth: 240 }}
    >
      <div className="flex items-center gap-2">
        <LightningGlyph />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] uppercase tracking-wider opacity-80">{sub}</div>
          <div
            className="truncate text-sm font-semibold"
            style={{ fontFamily: '"Source Serif Pro", Georgia, serif' }}
          >
            {data.label}
          </div>
        </div>
      </div>
      {/* Outgoing only — triggers are graph sources. */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{ background: '#B8965A', width: 8, height: 8, border: '2px solid #FAF8F3' }}
      />
    </div>
  );
}

function LightningGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4 shrink-0 text-brand-gold-300"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M11.3 1.046A1 1 0 0 1 12 2v6h4a1 1 0 0 1 .82 1.573l-7 10A1 1 0 0 1 8 19v-6H4a1 1 0 0 1-.82-1.573l7-10a1 1 0 0 1 1.12-.381Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
